import { animated, useSpring, useTransition } from '@react-spring/web';
import riveWASMResource from '@rive-app/canvas/rive.wasm?url';
import {
  Fit,
  Layout,
  RuntimeLoader,
  useRive,
  useStateMachineInput,
} from '@rive-app/react-canvas';
import Button from '@uikit/components/Button/Button';
import { ButtonColor, ShowIcon } from '@uikit/components/Button/consts';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import Icon from '@uikit/components/Icon/Icon';
import getEnergyGuzzlersFlatList from '../helpers/getEnergyGuzzlersFlatList';
import styles from './EnergyGuzzlersQuizSavingsCounter.module.scss';
import { EnergyGuzzlersQuizSavingsCounterProps } from './types';

RuntimeLoader.setWasmUrl(riveWASMResource);

const ENERGY_GUZZLERS_COUNT = getEnergyGuzzlersFlatList().length;

const AnimatedRiveComponent = animated(({ progress }: { progress: number }) => {
  const { rive, RiveComponent } = useRive({
    src: '/assets/energy-guzzlers/energiefresser-progress.riv',
    stateMachines: 'Motion',
    autoplay: true,
    layout: new Layout({ fit: Fit.Fill }),
  });
  const bumpInput = useStateMachineInput(rive, 'Motion', 'Position');

  if (bumpInput) {
    bumpInput.value = progress * 100;
  }

  return <RiveComponent />;
});

export default function EnergyGuzzlersQuizSavingsCounter({
  onRestartQuiz,
  savingsAmount,
  showRestartButton,
  step,
}: EnergyGuzzlersQuizSavingsCounterProps) {
  const countingNumber = useSpring({ val: savingsAmount, from: { val: 0 } });
  const countingNumberPretty = countingNumber.val.to((val) => Math.floor(val));

  const stepSpring = useSpring({
    val: (step - 1) / ENERGY_GUZZLERS_COUNT,
    from: { val: 0 },
    config: {
      tension: 50,
    },
  });

  const transitions = useTransition(showRestartButton, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    reverse: showRestartButton,
    exitBeforeEnter: true,
  });

  return (
    <div className={styles.base}>
      <div className={styles.background}>
        <AnimatedRiveComponent progress={stepSpring.val} />
        <Icon
          variant="user/lightning"
          size="iconSize128"
          className={styles.icon}
          isOutlined
        />
      </div>

      {transitions((animatedStyles, showButton) => {
        return (
          <>
            {!showButton && (
              <animated.div style={{ ...animatedStyles }}>
                <div className={styles.savingHint}>
                  Dein jährliches Sparpotenzial
                </div>
                <div className={styles.value}>
                  <animated.span>{countingNumberPretty}</animated.span>
                  <span className={styles.unit}>kWh</span>
                </div>
              </animated.div>
            )}
            {showButton && (
              <animated.div style={{ ...animatedStyles }}>
                <Button
                  actionType={ClickableAction.Custom}
                  onClick={onRestartQuiz}
                  icon="action/change"
                  showIcon={ShowIcon.ShowIcon}
                  color={ButtonColor.Primary}
                  className={styles.restartButton}
                >
                  Noch einmal spielen
                </Button>
              </animated.div>
            )}
          </>
        );
      })}
    </div>
  );
}
